import { Panel } from 'src/modules/shared/components/molecules/panel';
import { PanelHeader } from 'src/modules/shared/components/molecules/panel/panel-header';
import { Text } from 'src/modules/shared/components/atoms/text';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { PiPlus } from 'react-icons/pi';
import Loading from 'src/modules/shared/components/atoms/loading';
import { ErrorState } from 'src/modules/shared/components/atoms/states/error-state';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { FunnelEditCard } from '../../molecules/funnels/funnel-edit-card';
import { FunnelStatusManagementPanel } from './status-management-panel';
import { useFunnels } from 'src/modules/shared/hooks/api/crm/funnels/funnels';
import { FunnelPermission } from 'src/modules/shared/types/permissions.type';
import { useHasPermissions } from 'src/modules/shared/hooks/auth';
import { useEffect, useState } from 'react';
import { FunnelForm } from 'src/modules/settings/components/molecules/funnels/funnel-form';
import type { FunnelFormValues } from 'src/modules/settings/components/molecules/funnels/funnel-form';
import { useCreateFunnel } from 'src/modules/shared/hooks/api/crm/funnels/funnel.mutation';
import { FunnelCard } from 'src/modules/settings/components/atoms/funnels/funnel-card';
import { useAlert } from 'src/contexts/alert-context';
import { useDirtyMap } from 'src/modules/shared/hooks/dirty-state';
import { FunnelSectionsVisibilityProvider } from 'src/modules/settings/contexts/funnels/sections-visibility.context';

const DIRTY_KEY_CREATING = 'creating' as const;
const DIRTY_KEY_FUNNEL = (id: number | string) => `funnel:${id}`;
const DIRTY_KEY_PANEL_STATUSES = (id: number | string) =>
  `panelStatusesDirty:${id}`;

interface FunnelManagementPanelProps {
  onHasUnsavedChanges?: (dirty: boolean) => void;
}

export const FunnelManagementPanel: React.FC<FunnelManagementPanelProps> = ({
  onHasUnsavedChanges,
}) => {
  const { showAlert } = useAlert();

  const { funnels, loading: isLoadingFunnels, error, refetch } = useFunnels();

  const { hasPermission: canCreate } = useHasPermissions(
    FunnelPermission.Create
  );

  const [isCreatingFunnel, setIsCreatingFunnel] = useState(false);
  const dirtyMap = useDirtyMap();

  const { createFunnel, loading: isCreatingLoading } = useCreateFunnel(
    () => {
      // TODO: check when implementing i18n
      showAlert('El embudo se creó correctamente', 'success');
    },
    (error) => {
      console.error(error);
      showAlert(
        // TODO: check when implementing i18n
        'Ocurrió un error al crear el embudo. Por favor, inténtalo de nuevo.',
        'error'
      );
    }
  );

  const handleFunnelCreate = async (data: FunnelFormValues) => {
    await createFunnel(data);
    setIsCreatingFunnel(false);
    dirtyMap.resetKey(DIRTY_KEY_CREATING);
  };

  const handleToggleCreatingFunnel = () => {
    setIsCreatingFunnel((prev) => {
      const next = !prev;

      const isFormClosing = prev;
      if (isFormClosing) {
        dirtyMap.resetKey(DIRTY_KEY_CREATING);
      }

      return next;
    });
  };

  const handleStartCreatingFunnel = () => {
    setIsCreatingFunnel(true);
  };

  useEffect(() => {
    onHasUnsavedChanges?.(dirtyMap.isAnyDirty);
  }, [dirtyMap.isAnyDirty, onHasUnsavedChanges]);

  if (isLoadingFunnels) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loading />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4">
        <ErrorState
          // TODO: check when implementing i18n
          title="Error al cargar los embudos"
          description="No se pudieron cargar los embudos. Por favor, inténtalo de nuevo."
          onAction={refetch}
          actionContent="Reintentar"
        />
      </div>
    );
  }

  const showEmptyState = funnels.length === 0 && !isCreatingFunnel;

  return (
    <Panel className="w-full">
      <PanelHeader
        actions={
          canCreate && (
            <Button
              onClick={handleStartCreatingFunnel}
              variant="primary"
              size="md"
              className="flex items-center gap-2 mb-2"
            >
              <PiPlus className="w-4 h-4" />
              {/* TODO: check when implementing i18n */}
              <Text size="small" className="hidden md:inline">
                Nuevo Embudo
              </Text>
            </Button>
          )
        }
      >
        <></>
      </PanelHeader>

      <div className="p-4 overflow-auto h-[calc(100%-4rem)]">
        {showEmptyState ? (
          <div className="h-full flex flex-col items-center justify-center">
            <EmptyState
              // TODO: check when implementing i18n
              title="No hay embudos"
              // TODO: check when implementing i18n
              description="Agrega un embudo para comenzar"
              actionContent={
                canCreate && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={handleToggleCreatingFunnel}
                  >
                    <PiPlus className="w-4 h-4" />
                    {/* TODO: check when implementing i18n */}
                    <Text size="small">Nuevo embudo</Text>
                  </Button>
                )
              }
            />
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            {isCreatingFunnel && (
              <FunnelCard
                appearance="outline"
                variant="primary"
                className="my-2"
              >
                <div className="flex flex-col gap-8">
                  <Text size="h6" weight="medium">
                    {/* TODO: check when implementing i18n */}
                    Crea un nuevo embudo
                  </Text>

                  <FunnelSectionsVisibilityProvider>
                    <FunnelForm
                      id="funnel-create-form"
                      onSubmit={handleFunnelCreate}
                      editMode
                      isLoading={isCreatingLoading}
                      onCancel={handleToggleCreatingFunnel}
                      onDirtyChange={(d) =>
                        dirtyMap.setDirty(DIRTY_KEY_CREATING, d)
                      }
                    />
                  </FunnelSectionsVisibilityProvider>
                </div>
              </FunnelCard>
            )}

            {funnels.map((funnel) => (
              <FunnelEditCard
                key={funnel.id}
                funnel={funnel}
                onDirtyChange={(dirty) =>
                  dirtyMap.setDirty(DIRTY_KEY_FUNNEL(funnel.id), dirty)
                }
                onEditingChange={(editing) => {
                  if (editing) return;

                  dirtyMap.resetKey(DIRTY_KEY_PANEL_STATUSES(funnel.id));
                }}
              >
                {(isEditing) => (
                  <FunnelStatusManagementPanel
                    isEditing={isEditing}
                    funnelId={funnel.id}
                    onHasUnsavedChanges={(d) =>
                      dirtyMap.setDirty(DIRTY_KEY_PANEL_STATUSES(funnel.id), d)
                    }
                  />
                )}
              </FunnelEditCard>
            ))}
          </div>
        )}
      </div>
    </Panel>
  );
};
